import type {
  AppNotesAttachment,
  AppNotesComment,
  AppNotesProject,
  AppNotesRole,
  AppNotesThread,
  AppNotesUser,
  AuthResult,
  CreateThreadInput as ContractCreateThreadInput,
  MessageResult,
  ProjectAccess,
  ProjectInvitation,
  ProjectInvitationPreview,
  ProjectInviteLink,
  ProjectMember,
  RegisterInput,
  RegistrationResult,
  RoomAccessRule,
  SdkAuthResult,
  ThreadListScope,
  ThreadUnreadSummary,
  UpdateThreadInput,
} from "@appnotes/contracts";

export type {
  AppNotesAttachment,
  AppNotesComment,
  AppNotesProject,
  AppNotesRole,
  AppNotesThread,
  AppNotesUser,
  AuthResult,
  MessageResult,
  ProjectAccess,
  ProjectInvitation,
  ProjectInvitationPreview,
  ProjectInviteLink,
  ProjectMember,
  RegisterInput,
  RegistrationResult,
  RoomAccessRule,
  SdkAuthResult,
  ThreadUnreadSummary,
  UpdateThreadInput,
} from "@appnotes/contracts";

export type CreateThreadInput = Omit<ContractCreateThreadInput, "projectKey">;
export type ThreadScope = ThreadListScope;

export type AppNotesClientOptions<Session extends SdkAuthResult = AuthResult> = {
  baseUrl: string;
  projectKey: string;
  getAccessToken?: () => string | null | Promise<string | null>;
  getRefreshToken?: () => string | null | Promise<string | null>;
  onSessionRefresh?: (session: Session) => void | Promise<void>;
  credentials?: RequestCredentials;
  authenticationScope?: "global" | "project";
  refreshMode?: "token" | "cookie";
  fetch?: typeof globalThis.fetch;
};

export type AppNotesIntegrationClientOptions = {
  baseUrl: string;
  projectKey: string;
  apiKey: string;
  fetch?: typeof globalThis.fetch;
};

export class AppNotesApiError extends Error {
  readonly status: number;
  readonly details: unknown;

  constructor(status: number, message: string, details?: unknown) {
    super(message);
    this.name = "AppNotesApiError";
    this.status = status;
    this.details = details;
  }
}

export type AppNotesHttpTransportOptions = {
  baseUrl: string;
  credentials?: RequestCredentials;
  fetch?: typeof globalThis.fetch;
};

export class AppNotesHttpTransport {
  private readonly baseUrl: string;
  private readonly credentials?: RequestCredentials;
  private readonly requestFetch: typeof globalThis.fetch;

  constructor(options: AppNotesHttpTransportOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.credentials = options.credentials;
    const requestFetch = options.fetch ?? globalThis.fetch;
    if (!requestFetch) throw new Error("AppNotes Fetch API is unavailable");
    this.requestFetch = requestFetch.bind(globalThis);
  }

  assetUrl(path: string | null | undefined): string | null {
    if (!path) return null;
    if (/^(?:https?:|data:)/.test(path)) return path;
    return `${this.baseUrl}/${path.replace(/^\/+/, "")}`;
  }

  async request<T>(
    path: string,
    options: RequestInit & { accessToken?: string; responseType?: "json" | "blob" } = {},
  ): Promise<T> {
    const { accessToken, responseType = "json", ...requestOptions } = options;
    const headers = new Headers(requestOptions.headers);
    headers.set("Accept", responseType === "blob" ? "*/*" : "application/json");
    if (requestOptions.body && !(requestOptions.body instanceof FormData)) {
      headers.set("Content-Type", "application/json");
    }
    if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
    const response = await this.requestFetch(`${this.baseUrl}${path}`, {
      ...(this.credentials ? { credentials: this.credentials } : {}),
      ...requestOptions,
      headers,
    });
    const body =
      responseType === "blob" && response.ok
        ? await response.blob()
        : await readResponseBody(response);
    if (!response.ok) {
      const rawMessage = isRecord(body) ? body.message : undefined;
      const message =
        typeof rawMessage === "string"
          ? rawMessage
          : Array.isArray(rawMessage) && rawMessage.every((item) => typeof item === "string")
            ? rawMessage.join(". ")
            : `AppNotes API returned ${response.status}`;
      throw new AppNotesApiError(
        response.status,
        message,
        isRecord(body) && "details" in body ? body.details : body,
      );
    }
    return body as T;
  }
}

export class AppNotesClient<Session extends SdkAuthResult = AuthResult> {
  private readonly projectKey: string;
  private readonly getAccessToken?: AppNotesClientOptions<Session>["getAccessToken"];
  private readonly getRefreshToken?: AppNotesClientOptions<Session>["getRefreshToken"];
  private readonly onSessionRefresh?: AppNotesClientOptions<Session>["onSessionRefresh"];
  private readonly authenticationScope: "global" | "project";
  private readonly refreshMode: "token" | "cookie";
  private readonly transport: AppNotesHttpTransport;
  private refreshPromise: Promise<Session> | null = null;
  private lastRefreshedSession: Session | null = null;

  constructor(options: AppNotesClientOptions<Session>) {
    this.projectKey = options.projectKey;
    this.getAccessToken = options.getAccessToken;
    this.getRefreshToken = options.getRefreshToken;
    this.onSessionRefresh = options.onSessionRefresh;
    this.authenticationScope = options.authenticationScope ?? "global";
    this.refreshMode = options.refreshMode ?? "token";
    this.transport = new AppNotesHttpTransport(options);
  }

  assetUrl(path: string | null | undefined): string | null {
    return this.transport.assetUrl(path);
  }

  login(email: string, password: string, otp?: string): Promise<Session> {
    return this.request<Session>("/auth/login", {
      method: "POST",
      body: JSON.stringify({
        email,
        password,
        ...(this.authenticationScope === "project" ? { projectKey: this.projectKey } : {}),
        ...(otp ? { otp } : {}),
      }),
    });
  }

  refresh(refreshToken: string): Promise<Session> {
    return this.request<Session>("/auth/refresh", {
      method: "POST",
      body: JSON.stringify({ refreshToken }),
    });
  }

  register(input: RegisterInput): Promise<RegistrationResult> {
    return this.request<RegistrationResult>("/auth/register", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  verifyEmail(token: string): Promise<Session & { projectKey: string }> {
    return this.request<Session & { projectKey: string }>("/auth/verify-email", {
      method: "POST",
      body: JSON.stringify({ token }),
    });
  }

  resendEmailVerification(email: string): Promise<MessageResult> {
    return this.request<MessageResult>("/auth/resend-verification", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  requestPasswordReset(email: string): Promise<MessageResult> {
    return this.request<MessageResult>("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  resetPassword(token: string, password: string): Promise<MessageResult> {
    return this.request<MessageResult>("/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    });
  }

  getProjectInvitation(token: string): Promise<{ invitation: ProjectInvitationPreview }> {
    return this.request(`/auth/invitations/${encodeURIComponent(token)}`);
  }

  acceptProjectInvitation(token: string): Promise<{ member: ProjectMember }> {
    return this.request("/auth/invitations/accept", {
      authenticated: true,
      method: "POST",
      body: JSON.stringify({ token }),
    });
  }

  me(): Promise<{ user: AppNotesUser }> {
    return this.request<{ user: AppNotesUser }>("/auth/me", { authenticated: true });
  }

  logout(): Promise<void> {
    return this.request<void>("/auth/logout", {
      authenticated: true,
      method: "POST",
      ...(this.authenticationScope === "project"
        ? { body: JSON.stringify({ projectKey: this.projectKey }) }
        : {}),
    });
  }

  listProjects(): Promise<{ projects: ProjectAccess[] }> {
    return this.request<{ projects: ProjectAccess[] }>("/projects", {
      authenticated: true,
    });
  }

  createProject(name: string): Promise<ProjectAccess> {
    return this.request<ProjectAccess>("/projects", {
      authenticated: true,
      method: "POST",
      body: JSON.stringify({ name }),
    });
  }

  getProject(): Promise<ProjectAccess> {
    return this.request<ProjectAccess>(`/projects/${encodeURIComponent(this.projectKey)}`, {
      authenticated: true,
    });
  }

  listProjectMembers(): Promise<{ members: ProjectMember[] }> {
    return this.request<{ members: ProjectMember[] }>(
      `/projects/${encodeURIComponent(this.projectKey)}/members`,
      { authenticated: true },
    );
  }

  addProjectMember(
    email: string,
    role: Exclude<AppNotesRole, "owner">,
  ): Promise<{
    invitation: ProjectInvitation;
  }> {
    return this.request(`/projects/${encodeURIComponent(this.projectKey)}/members`, {
      authenticated: true,
      method: "POST",
      body: JSON.stringify({ email, role }),
    });
  }

  createProjectInviteLink(
    role: Exclude<AppNotesRole, "owner">,
  ): Promise<{ inviteLink: ProjectInviteLink }> {
    return this.request(`/projects/${encodeURIComponent(this.projectKey)}/invite-links`, {
      authenticated: true,
      method: "POST",
      body: JSON.stringify({ role }),
    });
  }

  updateProjectMemberRole(
    userId: string,
    role: Exclude<AppNotesRole, "owner">,
  ): Promise<{ member: ProjectMember }> {
    return this.request(
      `/projects/${encodeURIComponent(this.projectKey)}/members/${encodeURIComponent(userId)}`,
      {
        authenticated: true,
        method: "PATCH",
        body: JSON.stringify({ role }),
      },
    );
  }

  updateProjectMemberRoomAccess(
    userId: string,
    roomAccess: RoomAccessRule,
  ): Promise<{ member: ProjectMember }> {
    return this.request(
      `/projects/${encodeURIComponent(this.projectKey)}/members/${encodeURIComponent(userId)}/room-access`,
      {
        authenticated: true,
        method: "PATCH",
        body: JSON.stringify(roomAccess),
      },
    );
  }

  removeProjectMember(userId: string): Promise<{ member: ProjectMember }> {
    return this.request(
      `/projects/${encodeURIComponent(this.projectKey)}/members/${encodeURIComponent(userId)}`,
      {
        authenticated: true,
        method: "DELETE",
      },
    );
  }

  updateAllowedOrigins(allowedOrigins: string[]): Promise<{ project: AppNotesProject }> {
    return this.request(`/projects/${encodeURIComponent(this.projectKey)}/origins`, {
      authenticated: true,
      method: "PATCH",
      body: JSON.stringify({ allowedOrigins }),
    });
  }

  listThreads(input: {
    roomId: string;
    roomName?: string;
    pageId?: string;
    scope?: ThreadScope;
    search?: string;
    status?: AppNotesThread["status"];
    authorId?: string;
    pageBinding?: "any" | "current" | "unbound";
    cursor?: string;
    limit?: number;
  }): Promise<{
    items: AppNotesThread[];
    authors?: AppNotesUser[];
    nextCursor: string | null;
    unread?: ThreadUnreadSummary;
  }> {
    const query = new URLSearchParams({
      projectKey: this.projectKey,
      roomId: input.roomId,
      scope: input.scope ?? "all",
      limit: String(input.limit ?? 20),
    });
    if (input.roomName) query.set("roomName", input.roomName);
    if (input.pageId && (input.scope === "page" || input.pageBinding === "current")) {
      query.set("pageId", input.pageId);
    }
    if (input.search) query.set("search", input.search);
    if (input.status) query.set("status", input.status);
    if (input.authorId) query.set("authorId", input.authorId);
    if (input.pageBinding && (input.scope ?? "all") === "all") {
      query.set("pageBinding", input.pageBinding);
    }
    if (input.cursor) query.set("cursor", input.cursor);

    return this.request(`/threads?${query}`, { authenticated: true });
  }

  getThread(threadId: string): Promise<AppNotesThread> {
    return this.request(`/threads/${encodeURIComponent(threadId)}`, {
      authenticated: true,
    });
  }

  markThreadRead(threadId: string, readThrough: string): Promise<AppNotesThread> {
    return this.request(`/threads/${encodeURIComponent(threadId)}/read`, {
      authenticated: true,
      method: "POST",
      body: JSON.stringify({ readThrough }),
    });
  }

  createThread(input: CreateThreadInput): Promise<AppNotesThread> {
    return this.request("/threads", {
      authenticated: true,
      method: "POST",
      body: JSON.stringify({ ...input, projectKey: this.projectKey }),
    });
  }

  updateThread(threadId: string, input: UpdateThreadInput): Promise<AppNotesThread> {
    return this.request(`/threads/${encodeURIComponent(threadId)}`, {
      authenticated: true,
      method: "PATCH",
      body: JSON.stringify(input),
    });
  }

  addComment(threadId: string, body: string): Promise<AppNotesComment> {
    return this.request(`/threads/${encodeURIComponent(threadId)}/comments`, {
      authenticated: true,
      method: "POST",
      body: JSON.stringify({ body }),
    });
  }

  uploadAttachment(
    threadId: string,
    file: Blob,
    options: { commentId?: string; fileName?: string } = {},
  ): Promise<{ attachment: AppNotesAttachment; thread: AppNotesThread }> {
    const form = new FormData();
    form.set(
      "file",
      file,
      options.fileName ??
        (typeof File !== "undefined" && file instanceof File ? file.name : "attachment"),
    );
    const target = options.commentId
      ? `/threads/${encodeURIComponent(threadId)}/comments/${encodeURIComponent(options.commentId)}/attachments`
      : `/threads/${encodeURIComponent(threadId)}/attachments`;
    return this.request(target, {
      authenticated: true,
      method: "POST",
      body: form,
    });
  }

  deleteAttachment(attachmentId: string): Promise<AppNotesThread> {
    return this.request(`/attachments/${encodeURIComponent(attachmentId)}`, {
      authenticated: true,
      method: "DELETE",
    });
  }

  downloadAttachment(attachmentId: string): Promise<Blob> {
    return this.request(`/attachments/${encodeURIComponent(attachmentId)}/content`, {
      authenticated: true,
      responseType: "blob",
    });
  }

  setThreadStatus(threadId: string, status: AppNotesThread["status"]): Promise<AppNotesThread> {
    return this.request(`/threads/${encodeURIComponent(threadId)}/status`, {
      authenticated: true,
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
  }

  setThreadPinned(threadId: string, isPinned: boolean): Promise<AppNotesThread> {
    return this.request(`/threads/${encodeURIComponent(threadId)}/pinned`, {
      authenticated: true,
      method: "PATCH",
      body: JSON.stringify({ isPinned }),
    });
  }

  archiveThread(threadId: string): Promise<AppNotesThread> {
    return this.request(`/threads/${encodeURIComponent(threadId)}/archive`, {
      authenticated: true,
      method: "POST",
    });
  }

  restoreThread(threadId: string): Promise<AppNotesThread> {
    return this.request(`/threads/${encodeURIComponent(threadId)}/restore`, {
      authenticated: true,
      method: "POST",
    });
  }

  async clearArchive(roomId: string): Promise<number> {
    const query = new URLSearchParams({
      projectKey: this.projectKey,
      roomId,
    });
    const response = await this.request<{ cleared: number }>(`/threads/archive?${query}`, {
      authenticated: true,
      method: "DELETE",
    });
    return response.cleared;
  }

  private async request<T>(
    path: string,
    options: RequestInit & { authenticated?: boolean; responseType?: "json" | "blob" } = {},
    retryAfterRefresh = true,
    accessTokenOverride?: string,
  ): Promise<T> {
    const { authenticated, responseType = "json", ...requestOptions } = options;

    let authenticatedToken: string | null = null;
    if (authenticated) {
      const token = accessTokenOverride ?? (await this.getAccessToken?.());
      if (!token) {
        throw new AppNotesApiError(401, "Требуется авторизация");
      }
      authenticatedToken = token;
    }
    try {
      return await this.transport.request<T>(path, {
        ...requestOptions,
        accessToken: authenticatedToken ?? undefined,
        responseType,
      });
    } catch (error) {
      if (
        !(error instanceof AppNotesApiError) ||
        error.status !== 401 ||
        !authenticated ||
        !retryAfterRefresh ||
        (!this.getRefreshToken && this.refreshMode !== "cookie")
      ) {
        throw error;
      }
      const currentToken = await this.getAccessToken?.();
      if (currentToken && currentToken !== authenticatedToken) {
        return this.request<T>(path, options, false, currentToken);
      }
      const lastRefreshedSession = this.lastRefreshedSession;
      if (
        lastRefreshedSession &&
        lastRefreshedSession.accessToken !== authenticatedToken &&
        Date.parse(lastRefreshedSession.expiresAt) > Date.now()
      ) {
        return this.request<T>(path, options, false, lastRefreshedSession.accessToken);
      }
      const session = await this.refreshSession();
      return this.request<T>(path, options, false, session.accessToken);
    }
  }

  private refreshSession(): Promise<Session> {
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = (
      this.refreshMode === "cookie"
        ? this.request<Session>(
            "/auth/refresh",
            {
              method: "POST",
              body: JSON.stringify({ projectKey: this.projectKey }),
            },
            false,
          )
        : Promise.resolve(this.getRefreshToken?.()).then((refreshToken) => {
            if (!refreshToken) {
              throw new AppNotesApiError(401, "Требуется авторизация");
            }
            return this.refresh(refreshToken);
          })
    )
      .then(async (session) => {
        this.lastRefreshedSession = session;
        await this.onSessionRefresh?.(session);
        return session;
      })
      .finally(() => {
        this.refreshPromise = null;
      });
    return this.refreshPromise;
  }
}

/** Server-side client for the personal API-key integration endpoints. */
export class AppNotesIntegrationClient {
  private readonly projectKey: string;
  private readonly apiKey: string;
  private readonly transport: AppNotesHttpTransport;

  constructor(options: AppNotesIntegrationClientOptions) {
    this.projectKey = options.projectKey;
    this.apiKey = options.apiKey;
    this.transport = new AppNotesHttpTransport(options);
  }

  createNote(input: CreateThreadInput): Promise<AppNotesThread> {
    return this.request("/v1/notes", {
      method: "POST",
      body: JSON.stringify({ ...input, projectKey: this.projectKey }),
    });
  }

  updateNote(noteId: string, input: UpdateThreadInput): Promise<AppNotesThread> {
    return this.request(`/v1/notes/${encodeURIComponent(noteId)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  }

  addComment(noteId: string, body: string): Promise<AppNotesComment> {
    return this.request(`/v1/notes/${encodeURIComponent(noteId)}/comments`, {
      method: "POST",
      body: JSON.stringify({ body }),
    });
  }

  updateComment(noteId: string, commentId: string, body: string): Promise<AppNotesComment> {
    return this.request(
      `/v1/notes/${encodeURIComponent(noteId)}/comments/${encodeURIComponent(commentId)}`,
      {
        method: "PATCH",
        body: JSON.stringify({ body }),
      },
    );
  }

  uploadAttachment(
    noteId: string,
    file: Blob,
    options: { commentId?: string; fileName?: string } = {},
  ): Promise<{ attachment: AppNotesAttachment; thread: AppNotesThread }> {
    const form = new FormData();
    form.set(
      "file",
      file,
      options.fileName ??
        (typeof File !== "undefined" && file instanceof File ? file.name : "attachment"),
    );
    const target = options.commentId
      ? `/v1/notes/${encodeURIComponent(noteId)}/comments/${encodeURIComponent(options.commentId)}/attachments`
      : `/v1/notes/${encodeURIComponent(noteId)}/attachments`;
    return this.request(target, { method: "POST", body: form });
  }

  deleteAttachment(attachmentId: string): Promise<AppNotesThread> {
    return this.request(`/v1/attachments/${encodeURIComponent(attachmentId)}`, {
      method: "DELETE",
    });
  }

  downloadAttachment(attachmentId: string): Promise<Blob> {
    return this.request(`/v1/attachments/${encodeURIComponent(attachmentId)}/content`, {
      responseType: "blob",
    });
  }

  private async request<T>(
    path: string,
    options: RequestInit & { responseType?: "json" | "blob" },
  ): Promise<T> {
    const { responseType = "json", ...requestOptions } = options;
    return this.transport.request<T>(path, {
      ...requestOptions,
      accessToken: this.apiKey,
      responseType,
    });
  }
}

async function readResponseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  try {
    const text = await response.text();
    if (!text) return undefined;
    if (response.headers.get("content-type")?.includes("application/json")) {
      return JSON.parse(text) as unknown;
    }
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  } catch {
    return undefined;
  }
}

function isRecord(input: unknown): input is Record<string, unknown> {
  return typeof input === "object" && input !== null;
}
